import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { ApiError, identityApi, type Profile } from "@/lib/api";
import { profileFromApi } from "@/lib/person-name";
import { Onboarding } from "@/pages/Onboarding";
import Logo from "./layout/Logo";

type Status =
  | { kind: "loading" }
  | { kind: "needs-onboarding" }
  | { kind: "ready"; profile: Profile };

export function RequireOnboarded() {
  const auth = useAuth();
  const { updateProfile } = useData();
  const [status, setStatus] = useState<Status>({ kind: "loading" });

  useEffect(() => {
    if (auth.status !== "authenticated") return;
    let cancelled = false;
    setStatus({ kind: "loading" });
    identityApi
      .getProfile()
      .then((profile) => {
        if (cancelled) return;
        updateProfile({
          ...profileFromApi(profile),
          email: profile.email,
          documentType: profile.documentType === "PASAPORTE" ? "PA" : profile.documentType,
          documentNumber: profile.documentNumber,
          kind: profile.documentType === "NIT" ? "juridica" : "natural",
          companyName: profile.documentType === "NIT" ? profile.fullName : null,
        });
        setStatus({ kind: "ready", profile });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setStatus({ kind: "needs-onboarding" });
        } else {
          // On unknown errors fall back to onboarding rather than locking the
          // user out — they can retry, and other failures bubble up there.
          console.error("profile fetch failed", err);
          setStatus({ kind: "needs-onboarding" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [auth.status, auth.status === "authenticated" ? auth.user.id : null, updateProfile]);

  if (status.kind === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Logo />
          <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
        </div>
      </div>
    );
  }

  if (status.kind === "needs-onboarding") {
    return (
      <Onboarding
        onComplete={(profile) => setStatus({ kind: "ready", profile })}
      />
    );
  }

  return <Outlet />;
}
